import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { FormPrevious } from 'grommet-icons';
import { Alert as AlertIcon } from 'grommet-icons/icons';
import {
  BeaconChainValidator,
  BeaconChainValidatorResponse,
  Props,
} from './types';
import { Text } from '../../pulsechain_components/Text';
import { useWeb3ReactLegacy } from '../../utils/web3-compat';
import ValidatorTable from './components/ValidatorTable';
import TopupPage from './components/TopupPage';
import Spinner from '../../pulsechain_components/Spinner';
import { PageTemplate } from '../../pulsechain_components/PageTemplate';
import {
  BEACONCHAIN_URL,
  PRICE_PER_VALIDATOR,
  EJECTION_PRICE,
  TICKER_NAME,
} from '../../utils/envVars';
import { AllowedNetworks, NetworkChainId } from '../ConnectWallet/web3Utils';
import { Alert } from '../../pulsechain_components/Alert';
import { Link } from '../../pulsechain_components/Link';
import MultiWalletButton from '../ConnectWallet/MultiWalletButton';

const Arrow = styled(props => (<FormPrevious {...props} />) as any)`
  position: absolute;
  left: 0;
  color: ${p => p.theme.blue.medium};
`;

const SubTextContainer = styled.div``;

const BackText = styled((Text as any) as any)`
  margin-top: -25px;
  cursor: pointer;
  :hover {
    text-decoration: underline;
  }
  position: relative;
  padding-left: 25px;
`;

const FakeLink = styled.span`
  color: blue;
  text-decoration: underline;
  cursor: pointer;
  display: inline;
`;

const WalletButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

const _TopUpPage: React.FC<Props> = () => {
  const { account, active, chainId, deactivate } = useWeb3ReactLegacy();
  const [validators, setValidators] = useState<BeaconChainValidator[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [validatorLoadError, setValidatorLoadError] = React.useState<boolean>(
    false
  );
  const [
    showDepositVerificationWarning,
    setShowDepositVerificationWarning,
  ] = React.useState(false);

  const { formatMessage } = useIntl();

  // an effect that fetches validators from beaconchain when the user connects or changes their wallet
  useEffect(() => {
    const fetchValidatorsForUserAddress = async () => {
      setLoading(true);

      // beaconchain API requires two fetches - one that gets the public keys for an Ethereum address, and one that
      // queries by the validators public keys

      const eth1Url = `${BEACONCHAIN_URL}/api/v1/validator/eth1/${account}`;

      fetch(eth1Url)
        .then(r => r.json())
        .then(
          ({
            data,
          }: {
            data: BeaconChainValidatorResponse[] | BeaconChainValidatorResponse;
          }) => {
            setShowDepositVerificationWarning(false);
            const response = Array.isArray(data) ? data : [data];

            // no validators for that user's wallet address
            if (response.length === 0) {
              setValidators([]);
              setLoading(false);
              return;
            }

            // query by public keys
            const pubKeysCommaDelineated = `${response
              .slice(0, 50)
              .map(validator => validator.pubkey)
              .join(',')}`;

            const validatorUrl = `${BEACONCHAIN_URL}/api/v1/validator/${pubKeysCommaDelineated}`;

            fetch(validatorUrl)
              .then(r => r.json())
              .then(({ data }: { data: BeaconChainValidator[] }) => {
                if (data.length === 0) {
                  setShowDepositVerificationWarning(true);
                }

                setValidators(data);
                setLoading(false);
              })
              .catch(error => {
                console.error(
                  '❌ [TopUp] Error fetching validator details:',
                  error
                );
                setLoading(false);
                setValidatorLoadError(true);
              });
          }
        )
        .catch(error => {
          console.error('❌ [TopUp] Error fetching ETH1 validators:', error);
          setLoading(false);
          setValidatorLoadError(true);
        });
    };

    const network = NetworkChainId[chainId as number];

    const isValidNetwork = Object.values(AllowedNetworks).includes(network);

    if (active && account && isValidNetwork) {
      fetchValidatorsForUserAddress();
    }
  }, [account, active, chainId]);

  const [
    selectedValidator,
    setSelectedValidator,
  ] = useState<BeaconChainValidator | null>(null);

  const handleConnect = useCallback((): void => {
    // Deactivate current wallet to allow reconnecting
    if (active) {
      deactivate();
      // Refresh the page after 1 second when changing wallets
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [deactivate, active]);

  const topUpPageContent = useMemo(() => {
    if (loading) {
      return <Spinner className="mt40" />;
    }

    if (!active) {
      return (
        <WalletButtonContainer>
          <MultiWalletButton />
        </WalletButtonContainer>
      );
    }
    if (validatorLoadError) {
      return (
        <Alert variant="error" className="my10">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <AlertIcon color="redLight" />
            <FormattedMessage defaultMessage="There was an error loading your validator information from Beaconcha.in" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <FormattedMessage
              defaultMessage="You can {reloadYourWallet} to try again, or refresh the page. If issue persists, let us know on {github}."
              values={{
                reloadYourWallet: (
                  <FakeLink onClick={deactivate}>
                    <FormattedMessage defaultMessage="reload your wallet" />
                  </FakeLink>
                ),
                github: (
                  <Link
                    inline
                    primary
                    to="https://gitlab.com/pulsechaincom/staking-launchpad/-/issues/new"
                  >
                    GitLab
                  </Link>
                ),
              }}
              description="{reloadYourWallet} is a link labeled 'reload your wallet'"
            />
          </div>
        </Alert>
      );
    }

    if (selectedValidator) {
      return (
        <TopupPage
          validator={selectedValidator}
          onBack={() => setSelectedValidator(null)}
        />
      );
    }

    return (
      <>
        {showDepositVerificationWarning && (
          <Alert variant="warning" className="my10">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <AlertIcon color="redLight" />
              <Text className="ml10">
                <FormattedMessage
                  defaultMessage="You may have an {TICKER_NAME} deposit that was accepted but is being
                    validated on chain. It will be available here when beaconcha.in
                    has confirmed 2048 blocks."
                  values={{ TICKER_NAME }}
                />
              </Text>
            </div>
          </Alert>
        )}
        <ValidatorTable
          validators={validators}
          setSelectedValidator={setSelectedValidator}
          handleConnect={handleConnect}
        />
      </>
    );
  }, [
    loading,
    validatorLoadError,
    selectedValidator,
    active,
    validators,
    showDepositVerificationWarning,
    deactivate,
    handleConnect,
  ]);

  return (
    <PageTemplate
      title={formatMessage({ defaultMessage: 'Top up a validator' })}
    >
      {/* render a "back button" if there's a selected validator */}
      {selectedValidator && (
        <BackText onClick={() => setSelectedValidator(null)} color="blueMedium">
          <Arrow />
          <FormattedMessage defaultMessage="All validators" />
        </BackText>
      )}

      <SubTextContainer className="mt20">
        <Text className="mt10">
          <FormattedMessage
            defaultMessage="You may need to top up your validator's balance for two important reasons. If your validator's effective balance is below {PRICE_PER_VALIDATOR} {TICKER_NAME} you won't be earning your full staker rewards. And if it drops as low as {EJECTION_PRICE} {TICKER_NAME} the system will eject your validator."
            values={{
              PRICE_PER_VALIDATOR: PRICE_PER_VALIDATOR.toLocaleString('en-US'),
              TICKER_NAME,
              EJECTION_PRICE: EJECTION_PRICE.toLocaleString('en-US'),
            }}
          />
        </Text>
        <Alert variant="info" className="my20">
          <FormattedMessage
            defaultMessage="{PRICE_PER_VALIDATOR} {TICKER_NAME} is the maximum effective validator balance. This means you won't earn more rewards if your validator's balance goes above {PRICE_PER_VALIDATOR}. However you will earn less if it dips below {PRICE_PER_VALIDATOR} {TICKER_NAME}."
            values={{
              PRICE_PER_VALIDATOR: PRICE_PER_VALIDATOR.toLocaleString('en-US'),
              TICKER_NAME,
            }}
          />{' '}
          <Link
            inline
            primary
            to="https://www.attestant.io/posts/understanding-validator-effective-balance/"
          >
            <FormattedMessage defaultMessage="More on effective balance" />
          </Link>
        </Alert>
      </SubTextContainer>

      {/* the main content for the topup page */}
      {topUpPageContent}
    </PageTemplate>
  );
};

export const TopUpPage = _TopUpPage;
